/* ⚠️ GENERATED from app/services/avatarFace.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarFace.ts — the face: twenty-three numbers, and where they come from.
//
// 🟥 THREE "LOOKS" IS THREE FACES. A person has a nose width AND a jaw AND an
//    eye spacing, and no menu of presets will ever contain them. The catalogue
//    ships each of these as a morph target pair (_lo / _hi) on the body — and
//    on the parts that cover the head — so a face is a vector of −1…1 numbers
//    rather than a choice from a list.
//
// 🟥 AND A PERSON SHOULD NOT HAVE TO DIAL TWENTY-THREE SLIDERS. The selfie is
//    read by a vision model on our server (server/functions: POST
//    /api/avatar/face), which returns exactly this vector. The builder then
//    exposes the six that people actually reach for, so anything the model got
//    wrong is one tap from being right.
//
// 🟢 THE SCHEMA LIVES HERE, NOT IN THE PROMPT. The server asks the model for a
//    structured object built from `FACE_DIMS`; the client validates what comes
//    back against the same list. A dimension added here appears in both without
//    either side being edited, and a model that invents a field is ignored.
//
// 🟢 PURE. A twin is generated for the web; the server imports the same list.

import { bodyProperties, BODY_PROMPT, normalizeBody } from './avatarBody';

/** Every face dimension, with the two words that describe its ends.
 *  The words are the vocabulary the vision model is given — they are what makes
 *  a number mean something to it. */
export const FACE_DIMS = [{
  key: 'headRound',
  lo: 'long oval face',
  hi: 'round face',
  group: 'shape'
}, {
  key: 'headWidth',
  lo: 'narrow face',
  hi: 'wide face',
  group: 'shape'
}, {
  key: 'headLength',
  lo: 'short face',
  hi: 'long face',
  group: 'shape'
}, {
  key: 'jawWidth',
  lo: 'narrow pointed jaw',
  hi: 'wide square jaw',
  group: 'shape'
}, {
  key: 'chin',
  lo: 'receding chin',
  hi: 'prominent chin',
  group: 'shape'
}, {
  key: 'chinLength',
  lo: 'short chin',
  hi: 'long chin',
  group: 'shape'
}, {
  key: 'cheekBone',
  lo: 'flat cheekbones',
  hi: 'high sharp cheekbones',
  group: 'shape'
}, {
  key: 'cheekFull',
  lo: 'hollow cheeks',
  hi: 'full round cheeks',
  group: 'shape'
}, {
  key: 'forehead',
  lo: 'short forehead',
  hi: 'tall forehead',
  group: 'shape'
}, {
  key: 'browRidge',
  lo: 'flat brow ridge',
  hi: 'strong protruding brow ridge',
  group: 'shape'
}, {
  key: 'noseWidth',
  lo: 'narrow nose',
  hi: 'wide nose',
  group: 'nose'
}, {
  key: 'noseLength',
  lo: 'short nose',
  hi: 'long nose',
  group: 'nose'
}, {
  key: 'noseBridge',
  lo: 'concave scooped bridge',
  hi: 'convex humped bridge',
  group: 'nose'
}, {
  key: 'noseTip',
  lo: 'downturned tip',
  hi: 'upturned tip',
  group: 'nose'
}, {
  key: 'nostrils',
  lo: 'narrow nostrils',
  hi: 'flared nostrils',
  group: 'nose'
}, {
  key: 'mouthWidth',
  lo: 'small narrow mouth',
  hi: 'wide mouth',
  group: 'mouth'
}, {
  key: 'lipUpper',
  lo: 'thin upper lip',
  hi: 'full upper lip',
  group: 'mouth'
}, {
  key: 'lipLower',
  lo: 'thin lower lip',
  hi: 'full lower lip',
  group: 'mouth'
}, {
  key: 'eyeSize',
  lo: 'small narrow eyes',
  hi: 'large open eyes',
  group: 'eyes'
}, {
  key: 'eyeSpacing',
  lo: 'close-set eyes',
  hi: 'wide-set eyes',
  group: 'eyes'
}, {
  key: 'eyeAngle',
  lo: 'downturned outer corners',
  hi: 'upturned outer corners',
  group: 'eyes'
}, {
  key: 'eyeFold',
  lo: 'flat smooth eyelid',
  hi: 'deep hooded eyelid crease',
  group: 'eyes'
}, {
  key: 'ears',
  lo: 'small ears',
  hi: 'large ears',
  group: 'shape'
}];
export const FACE_KEYS = FACE_DIMS.map(d => d.key);

/** The handful the builder shows. Everything else the model set stays set; these
 *  are the ones people reach for when a face is nearly right. */
export const FACE_SLIDERS = ['headRound', 'jawWidth', 'eyeSize', 'noseWidth', 'mouthWidth', 'lipUpper'];

/** The morph target a dimension drives, given its value. */
export function faceMorphName(key, value) {
  return `f_${key}_${value < 0 ? 'lo' : 'hi'}`;
}
export function faceMorphNames(key) {
  return [`f_${key}_lo`, `f_${key}_hi`];
}
const clamp1 = v => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n < -1 ? -1 : n > 1 ? 1 : n;
};

/**
 * A face vector from anything: unknown keys dropped, every value clamped to
 * −1…1, and — 🟥 NEVER `undefined` AND NEVER A ZERO WE DID NOT MEAN. A zero is
 * dropped rather than stored, so the document stays small and "no opinion"
 * and "exactly average" are the same thing, which they are.
 */
export function normalizeFace(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const key of FACE_KEYS) {
    const v = clamp1(raw[key]);
    if (v !== 0) out[key] = Math.round(v * 100) / 100;
  }
  return out;
}
export function isNeutralFace(face) {
  const f = normalizeFace(face);
  return Object.keys(f).length === 0;
}

/** A stable string for the key/hash — sorted, so field order never matters. */
export function faceKeyPart(face) {
  const f = normalizeFace(face);
  return FACE_KEYS.filter(k => f[k]).map(k => `${k}:${f[k]}`).join(',');
}

/** Blend toward a face, for a slider that nudges one dimension. */
export function withFaceDim(face, key, value) {
  if (!FACE_KEYS.includes(key)) return normalizeFace(face);
  return normalizeFace({
    ...normalizeFace(face),
    [key]: clamp1(value)
  });
}

/* ─── what the vision model is asked for ──────────────────────────────── */

/** The JSON schema the server hands OpenAI (structured outputs). Built from the
 *  list above so the two can never drift. */
export function faceJsonSchema(hairStyles, eyeColors, skinCount) {
  const properties = {};
  const required = [];
  for (const d of FACE_DIMS) {
    properties[d.key] = {
      type: 'number',
      description: `−1 = ${d.lo}, 0 = average, 1 = ${d.hi}`
    };
    required.push(d.key);
  }
  /* 🟢 THE BUILD IS READ IN THE SAME BREATH. It is the same photo and the same
     look at it, and a second call would double the cost to ask a question the
     model has already answered to itself. services/avatarBody.ts owns the list. */
  const body = bodyProperties();
  for (const k of Object.keys(body)) {
    properties[k] = body[k];
    required.push(k);
  }
  properties.skinTone = {
    type: 'integer',
    description: `skin tone on a light-to-deep ladder, 0 = lightest, ${skinCount - 1} = deepest`
  };
  properties.hairStyle = {
    type: 'string',
    enum: hairStyles,
    description: 'the closest hairstyle in the list'
  };
  properties.hairDark = {
    type: 'number',
    description: '0 = white/platinum hair, 1 = black hair'
  };
  properties.hairRed = {
    type: 'number',
    description: '0 = no red/auburn tone, 1 = strongly red/ginger'
  };
  properties.eyeColor = {
    type: 'string',
    enum: eyeColors
  };
  properties.build = {
    type: 'number',
    description: '−1 = slim, 0 = average, 1 = heavy'
  };
  properties.confidence = {
    type: 'number',
    description: '0 = no clear face in the photo, 1 = a clear front-facing face'
  };
  required.push('skinTone', 'hairStyle', 'hairDark', 'hairRed', 'eyeColor', 'build', 'confidence');
  return {
    type: 'object',
    properties,
    required,
    additionalProperties: false
  };
}

/** The instruction. Geometry, not demography: the model is asked how this face
 *  is PROPORTIONED against an average adult face, never to label the person. */
export const FACE_PROMPT = ['You are measuring a face so a 3D avatar can be shaped to resemble it.', 'For every listed dimension, estimate where this face sits between the two extremes, as a number from -1 to 1, where 0 is an average adult face.', 'Judge proportion and shape only. Do not describe, name, or categorise the person; do not guess their identity, ethnicity, or age.', 'Be decisive: most faces differ from average on most dimensions, so avoid answering 0 everywhere. Use the full range when a feature is clearly distinctive.', 'If the image has no clear face, or the face is turned far away, set confidence below 0.4 and answer 0 for every dimension.', BODY_PROMPT].join(' ');

/* ─── what comes back ─────────────────────────────────────────────────── */

/** The model's answer, checked. Anything missing or out of range is neutral. */
export function parseFaceReading(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const confidence = clamp01(raw.confidence);
  return {
    face: normalizeFace(raw),
    body: normalizeBody(raw),
    skinTone: Math.max(0, Math.round(Number(raw.skinTone) || 0)),
    hairStyle: typeof raw.hairStyle === 'string' ? raw.hairStyle : '',
    hairDark: clamp01(raw.hairDark),
    hairRed: clamp01(raw.hairRed),
    eyeColor: typeof raw.eyeColor === 'string' ? raw.eyeColor : '',
    build: clamp1(raw.build),
    confidence
  };
}
function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** Below this the reading is not used for the face — a photo with no clear face
 *  must not produce a confidently wrong one. */
export const FACE_MIN_CONFIDENCE = 0.4;
export function readingIsUsable(r) {
  return !!r && r.confidence >= FACE_MIN_CONFIDENCE;
}
